import React, {
  useMemo,
  useCallback,
  useEffect,
  useState,
  useRef
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatCurrency } from '../utils/services';
import {
  getWalletSummary as walletSummary,
  getWalletTransactions as walletTransactions,
  resetWalletTransactions,
  postWalletApply,
  postWalletRemove
} from '../burger-king-lib/redux/wallet/actions';
import { LocalStorage } from '../burger-king-lib/utils/storage.web';
import { LocalSessionKey } from '../burger-king-lib/constants/app-constants';

export function useWalletSummaryDispatch() {
  const dispatch = useDispatch();

  const getWalletSummary = useCallback(
    callback => {
      const userId = LocalStorage.get(LocalSessionKey.USER_ID);
      if (!userId) return;
      const params = new URLSearchParams({
        userId
      });
      dispatch(walletSummary(params.toString(), callback));
    },
    [dispatch]
  );

  return {
    getWalletSummary
  };
}

export function useWalletSummarySelector() {
  const walletState = useSelector(state => state.wallet || {});

  const isWalletApplied = useMemo(() => {
    return walletState?.isWalletApplied;
  }, [walletState?.isWalletApplied]);

  const walletAmount = useMemo(() => {
    const amount = walletState?.walletAmount || 0;
    const currency = walletState?.walletData?.currency;

    return formatCurrency(amount, currency);
  }, [walletState?.walletAmount, walletState?.walletData?.currency]);

  const isUseBKWalletDisabled = useMemo(() => {
    return (walletState?.walletAmount || 0) <= 0;
  }, [walletState?.walletAmount]);

  const walletRemainingBalance = useMemo(() => {
    const amount =
      (isWalletApplied
        ? walletState?.walletRemainingBalance
        : walletState?.walletAmount) || 0;
    const currency = walletState?.walletData?.currency;
    return formatCurrency(amount, currency);
  }, [
    walletState?.walletRemainingBalance,
    walletState?.walletData?.currency,
    walletState?.walletAmount,
    isWalletApplied
  ]);

  const { isWalletSummaryLoading, walletAmountIsVisible } = useMemo(() => {
    const showWallet = Boolean(walletState?.walletAmountIsVisible);
    const summaryLoading = Boolean(walletState?.isWalletDataLoading);

    return {
      isWalletSummaryLoading: summaryLoading,
      walletAmountIsVisible: showWallet
    };
  }, [walletState?.walletAmountIsVisible, walletState?.isWalletDataLoading]);

  return {
    isUseBKWalletDisabled,
    walletRemainingBalance,
    walletAmountIsVisible,
    isWalletSummaryLoading,
    walletAmount,
    recentTransactions: walletState?.walletData?.recentTransactions || []
  };
}

export function useApplyRemoveWallet() {
  const dispatch = useDispatch();

  const isWalletApplied = useSelector(
    state => state?.redisCart?.postCart?.content?.wallet_applied
  );

  const applyWalletBallance = useCallback(
    (...action) => dispatch(postWalletApply(...action)),
    [dispatch]
  );
  const removeWalletBallance = useCallback(
    (...action) => dispatch(postWalletRemove(...action)),
    [dispatch]
  );

  return {
    applyWalletBallance,
    removeWalletBallance,
    isWalletApplied
  };
}

const WALLET_TRANSACTION_TABS_LIST = ['ALL', 'DEBIT', 'CREDIT'];

export function useWalletTransactions() {
  const [filters, setFilters] = useState(() => ({
    page: 1,
    pageSize: 20,
    type: 'all'
  }));
  const [transactions, setTransactions] = useState([]);
  const dispatch = useDispatch();
  const loadingMoreRef = useRef(null);

  const walletState = useSelector(state => state?.wallet);

  const isWalletTransactionsLoading = useMemo(() => {
    return Boolean(walletState.isWalletTransactionsLoading);
  }, [walletState?.isWalletTransactionsLoading]);

  const hasNextPage = useMemo(() => {
    return Boolean(walletState?.walletTransactionData?.hasNextPage);
  }, [walletState?.walletTransactionData?.hasNextPage]);

  const handleTabChange = (tab = '') => {
    const filterTab = (tab || '').toUpperCase();
    if (!WALLET_TRANSACTION_TABS_LIST.includes(filterTab)) return;

    setFilters(prev => ({ ...prev, type: tab, page: 1 }));
    setTransactions([]);
  };

  const getWalletTransactions = useCallback(async () => {
    const userId = LocalStorage.get(LocalSessionKey.USER_ID);
    if (!userId) return;

    const params = new URLSearchParams({ ...filters, userId });

    await dispatch(walletTransactions(params.toString()));
  }, [dispatch, filters]);

  const resetTransactionsHistory = () => {
    setTransactions([]);
    dispatch(resetWalletTransactions());
  };

  useEffect(() => {
    getWalletTransactions();
  }, [getWalletTransactions]);

  useEffect(() => {
    if (!loadingMoreRef.current) return undefined;

    const callback = entries => {
      const entry = entries[0];
      const { isIntersecting } = entry;

      if (isIntersecting && !isWalletTransactionsLoading && hasNextPage) {
        setFilters(prev => {
          return { ...prev, page: prev.page + 1 };
        });
      }
    };

    const observer = new IntersectionObserver(callback);

    observer.observe(loadingMoreRef.current);

    return () => observer.disconnect();
  }, [
    isWalletTransactionsLoading,
    walletState?.walletTransactionData?.hasNextPage
  ]);

  useEffect(() => {
    setTransactions(prev => [
      ...prev,
      ...(walletState?.walletTransactionData?.transactions || [])
    ]);
  }, [walletState?.walletTransactionData?.transactions]);

  return {
    loadingMoreRef,
    hasNextPage,
    filters,
    handleTabChange,
    resetTransactionsHistory,
    isWalletTransactionsLoading,
    transactions
  };
}

export const withWallet = Component => {
  return function MyComponent(props) {
    const { getWalletSummary } = useWalletSummaryDispatch();
    const {
      recentTransactions,
      walletAmount,
      isUseBKWalletDisabled,
      walletRemainingBalance
    } = useWalletSummarySelector();

    return (
      <Component
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        getWalletSummary={getWalletSummary}
        walletAmount={walletAmount}
        walletRemainingBalance={walletRemainingBalance}
        isUseBKWalletDisabled={isUseBKWalletDisabled}
        recentTransactions={recentTransactions}
      />
    );
  };
};

export const withApplyRemoveWallet = Component => {
  return function MyComponent(props) {
    const {
      applyWalletBallance,
      removeWalletBallance,
      isWalletApplied
    } = useApplyRemoveWallet();
    const { walletAmount } = useWalletSummarySelector();

    return (
      <Component
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        applyWalletBallance={applyWalletBallance}
        removeWalletBallance={removeWalletBallance}
        isWalletApplied={isWalletApplied}
        walletAmount={walletAmount}
      />
    );
  };
};
